import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink } from 'react-router';
import { Banner, Empty, Panel } from '../components/Panel';
import { api } from '../lib/api';
import { SRGuide } from './GuideSR';

/** Shape of GET /api/v1/signals/rules (only the parts this page uses). */
interface Rules {
  stochH1: {
    k_period: number;
    k_smoothing: number;
    d_smoothing: number;
    overbought: number;
    oversold: number;
    cross_window: number;
    zone_lookback: number;
    tp_usd: number;
    patterns: { name: string; direction: 'BUY' | 'SELL' }[];
  };
  stochH1Enabled: boolean;
  halftrendEnabled: boolean;
  v3Enabled: boolean;
  maxQuoteAgeSeconds: number;
}

function Rule({ n, title, children }: { n: number | string; title: string; children: ReactNode }) {
  return (
    <li className="guide-rule">
      <span className="guide-rule__n" aria-hidden="true">
        {n}
      </span>
      <div>
        <strong>{title}</strong>
        <div className="small">{children}</div>
      </div>
    </li>
  );
}

/** Guide of the H1 Stochastic + reversal candle alerts, with the settings in use. */
function StochGuide() {
  const q = useQuery({
    queryKey: ['signal-rules'],
    queryFn: () => api.get<Rules>('/api/v1/signals/rules'),
  });
  if (q.isLoading) return <Empty title="กำลังโหลด…" />;
  if (q.isError || !q.data) return <Empty title="โหลดค่ากฎไม่สำเร็จ">ติดต่อ backend ไม่ได้</Empty>;
  const s = q.data.stochH1;
  const stoch = `${s.k_period},${s.k_smoothing},${s.d_smoothing}`;
  const buys = s.patterns.filter((p) => p.direction === 'BUY').map((p) => p.name);
  const sells = s.patterns.filter((p) => p.direction === 'SELL').map((p) => p.name);

  return (
    <>
      <p className="page__lede">
        สัญญาณมาจากกฎ{' '}
        <strong>
          H1 Stochastic {s.overbought}/{s.oversold} + แท่งเทียนกลับตัว
        </strong>{' '}
        ใช้ราคาจาก MT5 ของคุณ — ค่าที่แสดงในหน้านี้ดึงจากระบบที่ทำงานอยู่จริง
      </p>

      <Banner tone="info" title="แจ้งเตือนเท่านั้น — ระบบไม่ส่งคำสั่งซื้อขาย">
        สัญญาณคือผลของกฎ ไม่ใช่การทำนาย ผลทดสอบย้อนหลังไม่รับประกันผลในอนาคต ไม่ใช่คำแนะนำการลงทุน
      </Banner>

      {!q.data.stochH1Enabled ? (
        <Banner tone="warn" title="กฎนี้ปิดอยู่">
          เปิดได้ด้วย SIGNAL_STOCH_H1_ENABLED=true
        </Banner>
      ) : null}

      <div className="stack" style={{ marginTop: 16 }}>
        <Panel title="1. ภาพรวม">
          <ul className="guide-list">
            <li>
              <strong>ไทม์เฟรม:</strong> H1 อย่างเดียว
            </li>
            <li>
              <strong>ตรวจเมื่อ:</strong> ทุกครั้งที่แท่ง H1 <strong>ปิด</strong> (ใช้เฉพาะแท่งที่ปิดแล้ว
              สัญญาณจึงไม่หายไปทีหลัง) แจ้งเตือนภายในไม่กี่วินาทีหลังแท่งปิด
            </li>
            <li>
              <strong>ราคา:</strong> แท่งเทียน Bid จาก MetaTrader 5 — คอมพิวเตอร์และ MT5 ต้องเปิดอยู่
            </li>
            <li>
              <strong>ครั้งละ 1 สัญญาณ:</strong> ถ้าสัญญาณก่อนหน้ายังไม่ถึง TP หรือ SL สัญญาณใหม่จะถูกข้าม
              (เหมือนตอนทดสอบย้อนหลัง)
            </li>
            <li>
              ดูสถานะล่าสุดได้ที่หน้า <Link to="/signals">สัญญาณ Stochastic</Link>
            </li>
          </ul>
        </Panel>

        <Panel title="2. เงื่อนไขที่ต้องผ่านทั้ง 2 ข้อ">
          <ol className="guide-rules">
            <Rule n={1} title={`Stochastic ${stoch} ตัดออกจากโซน ${s.overbought}/${s.oversold}`}>
              <strong>BUY:</strong> %K ตัดขึ้นเหนือ %D และ %K หรือ %D อยู่ต่ำกว่า {s.oversold} · <strong>SELL:</strong>{' '}
              %K ตัดลงใต้ %D และ %K หรือ %D อยู่สูงกว่า {s.overbought} — จุดตัดต้องอยู่ใน {s.cross_window} แท่ง H1
              ล่าสุด และอยู่ในโซนที่แท่งตัดหรือ {s.zone_lookback} แท่งก่อนหน้า
            </Rule>
            <Rule n={2} title="แท่ง H1 ที่เพิ่งปิดเป็นแท่งเทียนกลับตัวฝั่งเดียวกัน">
              <div>
                <strong>BUY:</strong> {buys.join(', ')}
              </div>
              <div>
                <strong>SELL:</strong> {sells.join(', ')}
              </div>
              <div className="muted">
                ({s.patterns.length} รูปแบบกลับตัวจาก 18 รูปแบบ — Marubozu, Three White Soldiers และ Three Black Crows
                เป็นแท่งตามเทรนด์ จึงไม่นับ)
              </div>
            </Rule>
          </ol>
        </Panel>

        <Panel title="3. จุดเข้า SL และ TP">
          <div className="table-wrap">
            <table className="ledger">
              <tbody>
                <tr>
                  <th scope="row">ราคาเข้า</th>
                  <td>ราคาตอนแท่ง H1 ปิด: BUY ที่ Ask · SELL ที่ Bid</td>
                </tr>
                <tr>
                  <th scope="row">Stop Loss</th>
                  <td>ปลายไส้ของแท่งสัญญาณ: BUY = Low ของแท่ง · SELL = High ของแท่ง + spread — ไม่เลื่อนตลอดไม้</td>
                </tr>
                <tr>
                  <th scope="row">Take Profit</th>
                  <td>
                    ราคาเข้า ± ${s.tp_usd} (ที่ 0.01 lot = +$
                    {s.tp_usd.toFixed(2)})
                  </td>
                </tr>
                <tr>
                  <th scope="row">จบไม้เมื่อ</th>
                  <td>
                    ถึง <strong>TP</strong> หรือชน <strong>SL</strong> — ไม่มีหมดอายุ ไม่มีกรอง R:R
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="4. แจ้งเตือนและติดตามผล">
          <ul className="guide-list">
            <li>
              <strong>มีสัญญาณใหม่:</strong> แจ้งบนเว็บ แอปมือถือ (Web Push) และ LINE พร้อมราคาเข้า SL (ระยะ $) TP
              เหตุผล (ค่า Stochastic + ชื่อแท่งเทียน) และกำไร/ขาดทุนที่ 0.01 lot
            </li>
            <li>
              <strong>ระหว่างไม้:</strong> แจ้งเมื่อถึง TP หรือชน SL (ถ้าเปิดรับ &quot;ผล TP/SL&quot; ไว้)
            </li>
            <li>
              ติดตามผลด้วยแท่ง M1 จาก MT5 (ฝั่ง SELL ใช้ Bid + spread) ถ้า TP กับ SL เกิดในนาทีเดียวกันนับว่าชน SL ก่อน
            </li>
            <li>
              เลือกช่องทางรับแจ้งเตือน (แอป / LINE / เบราว์เซอร์) ได้ที่หน้า <Link to="/settings">Settings</Link>
            </li>
            <li>Excel (หน้าสัญญาณ Stochastic) เก็บทุกสัญญาณพร้อมเหตุผลและผล</li>
            <li>ระบบไม่มีกฎข่าว — ควรดูปฏิทินข่าวเองก่อนตัดสินใจ</li>
          </ul>
        </Panel>

        <Panel title="5. ผลทดสอบย้อนหลัง (ข้อมูล MT5)">
          <ul className="guide-list">
            <li>
              ทดสอบ 4 ช่วง ช่วงละ 3 เดือน (ก.ค.–ต.ค. 2024, ก.ค.–ต.ค. 2025, เม.ย.–ก.ค. 2026, ก.ค.–ต.ค. 2026) ครั้งละ 1
              ไม้ หัก spread และค่าคอมมิชชัน
            </li>
            <li>สัญญาณประมาณ 22–29 ครั้งต่อเดือน (ราว 1 ครั้งต่อวัน)</li>
            <li>ถึง TP 55–77% · กำไรใน 3 จาก 4 ช่วง · ขาดทุนสะสมสูงสุดราว $22–85 ที่ 0.01 lot</li>
            <li>ระยะ SL ไม่คงที่ (ขึ้นกับไส้แท่ง) บางไม้ SL ไกลกว่า TP หลายเท่า — ไม้ที่ชน SL อาจลบกำไรหลายไม้</li>
          </ul>
        </Panel>

        <Panel title="ระบบอื่นที่ปิดอยู่">
          <p className="small muted">
            HalfTrend {q.data.halftrendEnabled ? 'เปิดอยู่ (SIGNAL_HALFTREND_ENABLED=true)' : 'ปิดอยู่'} · ระบบ V3 เดิม{' '}
            {q.data.v3Enabled ? 'เปิดอยู่ (SIGNAL_V3_ENABLED=true)' : 'ปิดอยู่'} — เก็บโค้ดไว้ เปิดได้ด้วยค่าตั้งในไฟล์
            .env
          </p>
        </Panel>
      </div>
    </>
  );
}

/** Admin guide of both alert systems: H1 Stochastic (/admin/guide) and support/resistance (/admin/guide/sr). */
export function GuidePage({ system = 'stoch' }: { system?: 'stoch' | 'sr' }) {
  return (
    <>
      <header className="page__header">
        <h1>คู่มือ: ระบบพิจารณาสัญญาณอย่างไร</h1>
      </header>
      <nav className="signal-tabs" aria-label="คู่มือระบบสัญญาณ">
        <NavLink to="/admin/guide" end>
          คู่มือ Stochastic
        </NavLink>
        <NavLink to="/admin/guide/sr">คู่มือแนวรับแนวต้าน</NavLink>
        <NavLink to="/guide/sto-h4">คู่มือ Sto-H4</NavLink>
      </nav>
      {system === 'sr' ? <SRGuide /> : <StochGuide />}
    </>
  );
}
